import React, { FC, useContext, useState, useEffect } from "react"
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"

import Routes from "@/routes"
import { ComponentType, newComponent } from "@/onboarding/features/product/models/Component"
import { ReservedFields, ReservedSections } from "@/onboarding/features/product/models/Product"
import { appendFieldListItem } from "@/onboarding/features/product/Slice"
import {
  getMediaItems,
  getOriginalMediaItems,
  getSectionSettings,
  getSettings,
} from "@/onboarding/features/product/selectors"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import EditIcon from "@/onboarding/features/product/components/shared/EditIcon"
import EditModal from "@/onboarding/features/product/components/shared/EditModal"
import { SubmittingContext } from "@/onboarding/features/product/helpers"
import SubmittingSpinner from "@/onboarding/features/product/components/shared/SubmittingSpinner"
import { useModal } from "@/helpers/useModal"
import ErrorBoundary from "@/components/ErrorBoundary"
import { isBlank } from "@/utils"

import Edit from "./Edit"

const MediaTile = ({ media, editable = true, openOnMount = false }) => {
  const toggleEdit = useModal((props) => (
    <EditModal {...props} title="Media" Body={Edit} bodyProps={{ media }} />
  ))

  // Automatically open the modal
  useEffect(() => {
    if (openOnMount && editable) {
      toggleEdit()
    }
  }, [openOnMount, editable, toggleEdit])

  // TODO(product): We should render some sort of "missing" png here
  const mediaPath = isBlank(media.componentAttributes.imageSrc)
    ? null
    : Routes.file_holder_path({ id: media.componentAttributes.imageSrc })

  return (
    <div
      className="d-flex flex-column border shadow-sm bg-body rounded p-1"
      style={{ width: "512px" }}
    >
      <OverlayTrigger
        placement="bottom"
        overlay={<Tooltip id={`media-tooltip-${media.name}`}>{media.description}</Tooltip>}
      >
        <video style={{ height: "288px" }} poster={mediaPath} muted autoPlay>
          <source src={mediaPath} type="video/mp4" />
        </video>
      </OverlayTrigger>
      <div className="d-flex justify-content-between gap-2">
        <div className="d-flex gap-2">
          <span>
            <b>
              <TextDisplay value={media.name} />
            </b>
          </span>
          {media.promotional && <span>(Promotional)</span>}
        </div>
        <div className="d-flex gap-2">
          <div>
            <a href={mediaPath} download className="cursor-pointer">
              <i className="fas fa-download" />
            </a>
          </div>
          {editable && <EditIcon onEdit={toggleEdit} />}
          {!_.isEmpty(media.issues) && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id={`media-tooltip-${media.name}-issues`}>
                  {media.issueDescription}
                </Tooltip>
              }
            >
              <div>
                <span className="text-danger">
                  <i className="fas fa-exclamation-triangle" />
                </span>
              </div>
            </OverlayTrigger>
          )}
        </div>
      </div>
    </div>
  )
}

const Content: FC = () => {
  const dispatch = useDispatch()
  const { tour_images } = useSelector(getSettings(ReservedFields.TOUR_IMAGES))
  const section = useSelector(getSectionSettings(ReservedSections.TOUR_IMAGES))
  const isWriteable = (tour_images?.writeable ?? false) && section?.writeable === true
  const isChanged = tour_images?.changed ?? false
  const isRequired = tour_images?.required ?? false
  const [submitting, setSubmitting] = useContext(SubmittingContext)
  const [showPrevious, setShowPrevious] = useState(false)
  const prompt = isRequired
    ? "No media loaded. This is a required component."
    : "No media loaded for this tour"

  const [newMediaIndex, setNewMediaIndex] = useState<number | null>(null)
  const mediaItems = useSelector(getMediaItems)
  const originalMediaItems = useSelector(getOriginalMediaItems)
  const mediaComponents = showPrevious ? originalMediaItems : mediaItems

  const handleToggle = () => setShowPrevious(!showPrevious)

  const addMedia = () => {
    setSubmitting(true)

    dispatch(
      appendFieldListItem({
        fieldName: ReservedFields.TOUR_IMAGES,
        value: newComponent({ kind: ComponentType.IMAGE, defaultRateCategories: [] }),
      })
    )

    // Set the index for modal to open
    setNewMediaIndex(mediaComponents.length)
  }

  // Reset newMediaIndex after modal is triggered
  useEffect(() => {
    if (newMediaIndex !== null && mediaComponents[newMediaIndex]) {
      setNewMediaIndex(null)
      setSubmitting(false)
    }
  }, [mediaComponents, newMediaIndex, setSubmitting])

  // TODO(tour-page): Figure out why this ErrorBoundary was added originally and remove if we're sure it's safe.
  return (
    <div className="d-flex flex-column gap-2">
      {isChanged && (
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleToggle}
          style={{ marginTop: "-38px" }}
          className="align-self-end me-5"
        >
          {showPrevious ? "View Updated" : "View Original"}
        </Button>
      )}
      {showPrevious && (
        <small className="ms-2 mb-2 text-muted fst-italic">Showing Original Component Values</small>
      )}
      {_.isEmpty(mediaComponents) && <div>{prompt}</div>}
      <div className="d-flex flex-wrap gap-3">
        {mediaComponents.map((item, index) => (
          <ErrorBoundary key={item.componentIndex}>
            <MediaTile media={item} editable={isWriteable} openOnMount={newMediaIndex === index} />
          </ErrorBoundary>
        ))}
      </div>
      {isWriteable && (
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-primary" onClick={addMedia} disabled={submitting}>
            <i className="fas fa-plus me-2" /> Add Media
          </Button>
          <SubmittingSpinner />
        </div>
      )}
    </div>
  )
}
export default Content
